import ProjectSummaryIcon from "@/assets/images/BidDetail/ai-project-summary-icon.png";
import BusinessRestrictIcon from "@/assets/images/BidDetail/business-restrict-icon.png";
import ManualProductionAnalysisIcon from "@/assets/images/BidDetail/manual-production-analytics-icon.png";
import ProjectInfoIcon from "@/assets/images/BidDetail/project-info-icon.png";
import Card from "./components/common/Card";
import { useReadProjectGroups } from "@/libs/react-query/hooks/useProjectQuery";
import { useReadProjectGroupsTasks } from "@/libs/react-query/hooks/useTaskQuery";
import { useNavigate, useParams } from "react-router-dom";
import DataTable from "@/components/Common/DataTable";
import { createColumns } from "./columns";
import ProjectUsers from "./components/ProjectUsers";
import CreateTaskDialog from "./components/CreateTaskDialog";
import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { ReactComponent as PlusIcon } from "@/assets/images/plus.svg";
import ProjectDetailFileManagement from "./components/ProjectDetailFileManagement";
import ProjectDetailBidNoticeCard from "./components/ProjectDetailBidNoticeCard";
import DetailCardItemWrapper from "@/components/DetailCardItemWrapper";
import DetailCardItem from "@/components/DetailCardItem";
import { DocumentType, ProjectInfo } from "@/types/project";
import { ProjectType } from "@/consts/Project";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { downloadFile, parseProjectTypeToBid, typeToTitle } from "../utils";
import DeleteProjectAlertDialog from "./components/DeleteProjectAlertDialog";
import ProjectDetailBidNoticeDialog from "./components/ProjectDetailBidNoticeDialog";
import { ReactComponent as ReplaceIcon } from "@/assets/images/replace.svg";
import { ReactComponent as ExportPageIcon } from "@/assets/images/page-export.svg";
import { calculateDDay, cn, formatDate } from "@/libs/utils";
import { AxiosError } from "axios";

export default function ProjectDetail() {
  const { group_id = "" } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: TasksData } = useReadProjectGroupsTasks(group_id);
  const {
    data: ProjectsData,
    isError,
    error,
  } = useReadProjectGroups(group_id);

  const { tasks = [] } = TasksData || {};
  const {
    users = [],
    projects = [],
    files = [],
    project_group_title = "",
  } = ProjectsData || {};

  const columns = createColumns(navigate, group_id);

  const findProjectByType = useCallback(
    (type: ProjectType) =>
      projects.find((project) => project.project_type === type),
    [projects]
  );

  if (isError) {
    const isForbidden = (error as AxiosError)?.response?.status === 403;
    return (
      <div className="mt-6 h-64 flex flex-col justify-center items-center gap-2 bg-white rounded-xl border border-gray-D">
        <p className="text-lg text-gray-B font-semibold">
          {isForbidden
            ? "프로젝트 참여자가 아닙니다"
            : "프로젝트 정보를 불러올 수 없습니다"}
        </p>
        {isForbidden && (
          <p className="text-sm text-gray-B">
            참여 중인 프로젝트만 상세 정보를 볼 수 있습니다.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="relative grid gap-5 mt-6">
      <Card icon={BusinessRestrictIcon} title="프로젝트 작업">
        <DataTable
          columns={columns}
          data={tasks}
          isPagination={false}
          containerClassName="max-h-[290px] overflow-y-auto"
        />
        <div className="flex justify-center py-2">
          <Button
            size="md"
            onClick={() => setOpen(true)}
            className="bg-dark-brown text-white hover:bg-dark-brown/90 gap-1 flex items-center"
          >
            <span className="text-xs font-bold">작업 생성하기</span>
            <PlusIcon className="size-[18px]" />
          </Button>
        </div>
        <CreateTaskDialog open={open} setOpen={setOpen} groupId={group_id} />
      </Card>

      <div className="grid grid-cols-[240px_auto] gap-5">
        <Card icon={ProjectSummaryIcon} title="프로젝트 참여자">
          <ProjectUsers users={users} />
        </Card>
        <Card icon={ManualProductionAnalysisIcon} title="파일 관리">
          <ProjectDetailFileManagement files={files} />
        </Card>
      </div>

      <div className="grid grid-cols-3 gap-x-11">
        {[ProjectType.PLAN, ProjectType.PRCR, ProjectType.PROJ].map((type) => (
          <Card
            key={type}
            icon={ProjectInfoIcon}
            title={typeToTitle(type)}
            containerClassName="h-fit relative"
          >
            {!findProjectByType(type) ? (
              <ProjectDetailBidNoticeCard
                type={type}
                defaultTitle={project_group_title}
              />
            ) : (
              <ProjectInfoWrapper
                project={findProjectByType(type)}
                defaultTitle={project_group_title}
              />
            )}
          </Card>
        ))}
      </div>

      <div className="absolute right-0 -top-12">
        <DeleteProjectAlertDialog />
      </div>
    </div>
  );
}

function ProjectInfoWrapper({
  project,
  defaultTitle,
}: {
  project?: ProjectInfo;
  defaultTitle: string;
}) {
  const navigate = useNavigate();

  const handleDownloadFile = useCallback((document: DocumentType) => {
    if (!document.document_url) return;

    downloadFile({
      file_name: document.document_title,
      download_url: document.document_url,
    });
  }, []);

  if (!project) {
    return (
      <p className="h-24 flex flex-col justify-center items-center text-lg text-gray-B font-semibold">
        정보를 찾을 수 없습니다
      </p>
    );
  }

  const handleRedirectPage = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(
      `/bid/domestic/detail/${parseProjectTypeToBid(project.project_type)}/${project.project_id}`
    );
  };

  const DialogTrigger = () => {
    return (
      <div className="flex items-center justify-end gap-4">
        <ReplaceIcon className="size-5 cursor-pointer" />
        {ProjectType.PLAN !== project.project_type && (
          <ExportPageIcon
            className="size-5 cursor-pointer"
            onClick={handleRedirectPage}
          />
        )}
      </div>
    );
  };

  const noticeNoTitle =
    project.project_type === ProjectType.PLAN
      ? "발주계획 번호"
      : project.project_type === ProjectType.PRCR
        ? "등록번호"
        : "공고번호";

  const noticeNoItems = project.notice_no
    ? {
        label: noticeNoTitle,
        value: project.notice_no || "-",
      }
    : null;

  const basis_amount_items =
    project.project_type === ProjectType.PROJ
      ? {
          label: "기초금액",
          value: project.basis_amount
            ? `${project.basis_amount.toLocaleString()}원`
            : "기초금액 정보 없음",
        }
      : null;

  const infoItems = [
    ...(noticeNoItems ? [noticeNoItems] : []),
    { label: "공고명", value: project.title },
    { label: "수요 기관", value: project.institution_name || "-" },
    {
      label: "배정예산",
      value: project.budget_amount
        ? `${project.budget_amount.toLocaleString()}원`
        : "공고서 참조",
    },
    ...(basis_amount_items ? [basis_amount_items] : []),
    {
      label: "시작일",
      value: project.begin_date ? formatDate(project.begin_date) : "-",
    },
    {
      label: "마감일",
      value: project.close_date ? (
        <span
          className={cn({
            "text-red font-bold":
              calculateDDay(project.close_date) <= 3 &&
              calculateDDay(project.close_date) >= 0, // D-3일 이하일 때 빨간색 강조
          })}
        >
          {formatDate(project.close_date)}
        </span>
      ) : (
        "-"
      ),
    },
  ];

  return (
    <div className="overflow-y-auto">
      <DetailCardItemWrapper>
        {infoItems.map((item) => (
          <DetailCardItem key={item.label} {...item} />
        ))}
      </DetailCardItemWrapper>

      {project.documents?.length && (
        <div className="mt-3">
          <Accordion
            type="single"
            collapsible
            className="w-full"
            defaultValue="documents"
          >
            <AccordionItem value="documents">
              <AccordionTrigger className="justify-end gap-1 text-[13px] font-normal text-gray-A opacity-60">
                첨부파일
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2">
                  {project.documents.map((document) => (
                    <p
                      key={document.document_id}
                      className="text-[10px] text-gray-A font-semibold cursor-pointer underline"
                      onClick={() => handleDownloadFile(document)}
                    >
                      {document.document_title}
                    </p>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}

      <div className="absolute top-5 right-5">
        <ProjectDetailBidNoticeDialog
          type={project.project_type}
          defaultTitle={defaultTitle}
          trigger={<DialogTrigger />}
        />
      </div>
    </div>
  );
}
